import {
  PREVIEW_SECTION_ATTR,
  PREVIEW_SLOT_ATTR,
  type SectionSlotView,
} from '@nopain-newsletter/core/sections';
import { PREVIEW_CSP } from '../html-template/previewDocument';

/**
 * Everything the editor canvas does to the mail inside its frame, as
 * plain functions on a `Document`, so it can be tested without a loaded
 * frame (jsdom does not load `srcdoc`).
 *
 * The frame is `sandbox="allow-same-origin"` with no `allow-scripts`:
 * nothing in the mail can run, submit, open a window or navigate the app,
 * but the editor (the parent page) may reach into the frame to mark the
 * selection, read where a click landed and write a slot's new value. The
 * Content-Security-Policy of the plain preview is kept as second layer.
 */

const SELECTED_ATTR = 'data-mf-selected';
const CURRENT_ATTR = 'data-mf-current';

const EDITOR_STYLE = [
  'html,body{margin:0}',
  'img{max-width:100%}',
  'a{cursor:pointer}',
  `[${PREVIEW_SLOT_ATTR}]{cursor:pointer;outline-offset:2px;scroll-margin:80px}`,
  `[${PREVIEW_SLOT_ATTR}]:hover{outline:1px dashed #5457d6}`,
  `[${PREVIEW_SLOT_ATTR}][${SELECTED_ATTR}]{outline:2px solid #5457d6}`,
  `[${PREVIEW_SECTION_ATTR}]{scroll-margin:24px}`,
  // An outline on a table row is not drawn everywhere; the cells carry it.
  `[${PREVIEW_SECTION_ATTR}][${CURRENT_ATTR}]>td,[${PREVIEW_SECTION_ATTR}][${CURRENT_ATTR}]>th{box-shadow:inset 0 0 0 2px #5457d6}`,
].join('');

const RENDER_META = 'mf-render';

/**
 * The document the canvas frame shows for a marked preview render.
 * `renderId` is written into it, so the editor can tell when the frame
 * holds this document and not the one before it.
 */
export function editorDocument(previewHtml: string, renderId: number): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    `<meta name="${RENDER_META}" content="${renderId}">` +
    `<meta http-equiv="Content-Security-Policy" content="${PREVIEW_CSP}">` +
    `<style>${EDITOR_STYLE}</style>` +
    `</head><body>${previewHtml}</body></html>`
  );
}

/** True once the frame has parsed the document of this render (its images may still be loading). */
export function holdsRender(doc: Document | null | undefined, renderId: number): doc is Document {
  if (!doc || doc.readyState === 'loading') return false;
  return (
    doc.querySelector(`meta[name="${RENDER_META}"]`)?.getAttribute('content') === String(renderId)
  );
}

function closestWith(target: EventTarget | null, attr: string): Element | null {
  let el = target as (Node & { parentElement?: Element | null }) | null;
  // A click can land on a text node's parent only, but be safe.
  if (el && el.nodeType !== 1) el = el.parentElement ?? null;
  return el ? (el as Element).closest(`[${attr}]`) : null;
}

/** What a click in the mail points at: the innermost slot, and the section whose row holds it. */
export function hitAt(target: EventTarget | null): {
  slotId: string | null;
  sectionId: string | null;
} {
  return {
    slotId: closestWith(target, PREVIEW_SLOT_ATTR)?.getAttribute(PREVIEW_SLOT_ATTR) ?? null,
    sectionId:
      closestWith(target, PREVIEW_SECTION_ATTR)?.getAttribute(PREVIEW_SECTION_ATTR) ?? null,
  };
}

function withAttr(doc: Document, attr: string, value: string): Element[] {
  // Ids are compared as values, never put into a selector.
  return [...doc.querySelectorAll(`[${attr}]`)].filter((el) => el.getAttribute(attr) === value);
}

/** Moves the selection marks to this section and slot. Returns the element to scroll to. */
export function markSelection(
  doc: Document,
  sectionId: string | null,
  slotId: string | null,
): Element | null {
  for (const el of doc.querySelectorAll(`[${SELECTED_ATTR}]`)) el.removeAttribute(SELECTED_ATTR);
  for (const el of doc.querySelectorAll(`[${CURRENT_ATTR}]`)) el.removeAttribute(CURRENT_ATTR);
  const rows = sectionId === null ? [] : withAttr(doc, PREVIEW_SECTION_ATTR, sectionId);
  for (const row of rows) row.setAttribute(CURRENT_ATTR, '');
  const slots = slotId === null ? [] : withAttr(doc, PREVIEW_SLOT_ATTR, slotId);
  for (const slot of slots) slot.setAttribute(SELECTED_ATTR, '');
  return slots[0] ?? rows[0] ?? null;
}

/**
 * Scrolls the mail so the element can be seen: a section from its top, a
 * slot in the middle. Does nothing when it is already fully in view, so
 * a click in the mail never moves the mail.
 */
export function reveal(el: Element, block: 'start' | 'center'): boolean {
  const height = el.ownerDocument.defaultView?.innerHeight ?? 0;
  const rect = el.getBoundingClientRect();
  if (rect.top >= 0 && rect.bottom <= height) return false;
  // A section taller than the frame that already fills it stays where it is.
  if (block === 'start' && rect.top <= 0 && rect.bottom >= height) return false;
  el.scrollIntoView?.({ block });
  return true;
}

/**
 * Writes a slot's current value into the frame without rendering the
 * mail again: the text of a text slot (line breaks as `<br>`), `src` and
 * `alt` of an image, `href` of a link. `displaySrc` maps an image address
 * to what the frame can load (the demo server keeps copies in memory).
 * Returns false when the slot is not in the frame (a hidden section).
 */
export function patchSlot(
  doc: Document,
  slot: SectionSlotView,
  displaySrc: (src: string) => string = (src) => src,
): boolean {
  const elements = withAttr(doc, PREVIEW_SLOT_ATTR, slot.id);
  for (const el of elements) {
    if (slot.kind === 'text') {
      const lines = (slot.values.text ?? '').split('\n');
      const nodes: Node[] = [];
      lines.forEach((line, i) => {
        if (i > 0) nodes.push(doc.createElement('br'));
        nodes.push(doc.createTextNode(line));
      });
      el.replaceChildren(...nodes);
    } else if (slot.kind === 'image') {
      el.setAttribute('src', displaySrc(slot.values.src ?? ''));
      if (slot.values.alt === null || slot.values.alt === undefined) el.removeAttribute('alt');
      else el.setAttribute('alt', slot.values.alt);
    } else {
      el.setAttribute('href', slot.values.href ?? '');
    }
  }
  return elements.length > 0;
}

/** Points every image at what the frame can load. Only the demo server needs it. */
export function mapImageSources(doc: Document, displaySrc: (src: string) => string): void {
  for (const img of doc.querySelectorAll('img[src]')) {
    const src = img.getAttribute('src') ?? '';
    const shown = displaySrc(src);
    if (shown !== src) img.setAttribute('src', shown);
  }
}
